import React, { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TendV2ContentCard from './TendV2ContentCard';
import { FontLibraryProvider } from '../../contexts/FontLibraryContext';
import { BrandPaletteProvider } from '../../contexts/BrandPaletteContext';
import TendedPlatformPreview, { TendedContentItem } from './TendedPlatformPreview';
import { tendApi, Tendv2ContentItem } from '../../api/tendApi';
import { Logo } from '../../api/logoApi';

interface SortableTendV2CardProps {
  item: Tendv2ContentItem;
  brandId?: number;
  brandName?: string;
  brandSlug?: string;
  contentId?: number | string;
  contentTitle?: string;
  brandDefaultLogo?: Logo | null;
  brandLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name';
  curatorBrandId?: number;
  onArchive?: (contentId: number) => void;
  onUnarchive?: (contentId: number) => void;
}

const SortableTendV2Card: React.FC<SortableTendV2CardProps> = ({
  item,
  brandId,
  brandName,
  brandSlug,
  contentId,
  contentTitle,
  brandDefaultLogo,
  brandLogoDisplayMode,
  curatorBrandId,
  onArchive,
  onUnarchive,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <TendV2ContentCard
        key={item.id}
        item={item}
        brandId={brandId}
        brandName={brandName}
        brandSlug={brandSlug}
        contentId={contentId}
        contentTitle={contentTitle}
        brandDefaultLogo={brandDefaultLogo}
        brandLogoDisplayMode={brandLogoDisplayMode}
        curatorBrandId={curatorBrandId}
        dragEnabled={true}
        dragHandleProps={{ ...attributes, ...listeners }}
        onArchive={onArchive}
        onUnarchive={onUnarchive}
      />
    </div>
  );
};

export interface TendedContentGridProps {
  contentId: number | string;
  activeTab: 'seed' | 'tendv2';
  items: Tendv2ContentItem[];
  seedItems?: TendedContentItem[];
  isLoading?: boolean;
  brandId?: number;
  brandName?: string;
  brandSlug: string;
  contentTitle?: string;
  brandDefaultLogo?: Logo | null;
  brandLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name';
  curatorBrandId?: number;
  isSuperAdmin?: boolean;
  onItemsChange: (items: Tendv2ContentItem[]) => void;
}

const TendedContentGrid: React.FC<TendedContentGridProps> = ({
  contentId,
  activeTab,
  items,
  seedItems = [],
  isLoading = false,
  brandId,
  brandName,
  brandSlug,
  contentTitle,
  brandDefaultLogo,
  brandLogoDisplayMode,
  curatorBrandId,
  isSuperAdmin = false,
  onItemsChange,
}) => {
  const [activeDragId, setActiveDragId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedItems = useMemo(() => {
    if (!isSuperAdmin) {
      return [...items]
        .filter(c => !c.is_archived)
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    }
    return [...items].sort((a, b) => {
      if (a.is_archived === b.is_archived) {
        return (a.display_order ?? 0) - (b.display_order ?? 0);
      }
      return a.is_archived ? 1 : -1;
    });
  }, [items, isSuperAdmin]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as number);
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveDragId(null);

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedItems.findIndex(item => item.id === active.id);
    const newIndex = sortedItems.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedItems, oldIndex, newIndex);
    const reorderedWithOrder = reordered.map((item, index) => ({
      ...item,
      display_order: index,
    }));

    onItemsChange(reorderedWithOrder);

    const updates = reorderedWithOrder.map((item, index) => ({
      id: item.id,
      display_order: index,
    }));

    try {
      await tendApi.updateContentOrder(updates);
      const numericId = typeof contentId === 'string' ? parseInt(contentId, 10) : contentId;
      const fresh = await tendApi.getTendV2ContentItems(numericId);
      onItemsChange(fresh);
    } catch (error) {
      console.error('Error persisting content order:', error);
      const numericId = typeof contentId === 'string' ? parseInt(contentId, 10) : contentId;
      const fresh = await tendApi.getTendV2ContentItems(numericId);
      onItemsChange(fresh);
    }
  }, [sortedItems, contentId, onItemsChange]);

  const activeDragItem = useMemo(() => {
    if (!activeDragId) return null;
    return sortedItems.find(item => item.id === activeDragId) || null;
  }, [activeDragId, sortedItems]);

  const handleArchive = useCallback(async (archiveId: number) => {
    try {
      const result = await tendApi.archiveContent(archiveId);
      if (result.success) {
        onItemsChange(
          items.map(item =>
            item.id === archiveId
              ? { ...item, is_archived: true, archived_at: result.archived_at || new Date().toISOString() }
              : item
          )
        );
      }
    } catch (error) {
      console.error('Error archiving content:', error);
    }
  }, [items, onItemsChange]);

  const handleUnarchive = useCallback(async (archiveId: number) => {
    try {
      const result = await tendApi.unarchiveContent(archiveId);
      if (result.success) {
        onItemsChange(
          items.map(item =>
            item.id === archiveId
              ? { ...item, is_archived: false, archived_at: null }
              : item
          )
        );
      }
    } catch (error) {
      console.error('Error unarchiving content:', error);
    }
  }, [items, onItemsChange]);

  if (activeTab === 'seed') {
    return (
      <>
        <div
          className="grid gap-6 transition-all duration-300"
          style={{
            gridTemplateColumns: `repeat(${Math.min(Math.max(seedItems.length, 1), 3)}, minmax(0, 1fr))`,
          }}
        >
          {seedItems.map((item, index) => (
            <TendedPlatformPreview
              key={`${item.platform.id}-${item.transformationId}-${index}`}
              item={item}
            />
          ))}
        </div>

        {seedItems.length === 0 && (
          <div className="text-center py-12">
            <span className="text-6xl mb-4 block">🌱</span>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No SEED content yet</h3>
            <p className="text-gray-500">Generate content using SEED to see results here.</p>
          </div>
        )}
      </>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-green-600 rounded-full mx-auto mb-4"></div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Loading TEND content...</h3>
        <p className="text-gray-500">Fetching from the server</p>
      </div>
    );
  }

  if (sortedItems.length === 0) {
    return (
      <div className="text-center py-12">
        <span className="text-6xl mb-4 block">🌱</span>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No TEND content yet</h3>
        <p className="text-gray-500">Use TEND to generate content</p>
      </div>
    );
  }

  return (
    <FontLibraryProvider brandSlug={brandSlug}>
    <BrandPaletteProvider brandId={brandId ?? null}>
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedItems.map(item => item.id)}
        strategy={rectSortingStrategy}
      >
        <div
          className="grid gap-5 transition-all duration-300"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          }}
        >
          {sortedItems.map((item) => (
            <SortableTendV2Card
              key={item.id}
              item={item}
              brandId={brandId}
              brandName={brandName}
              brandSlug={brandSlug}
              contentId={contentId}
              contentTitle={contentTitle}
              brandDefaultLogo={brandDefaultLogo}
              brandLogoDisplayMode={brandLogoDisplayMode}
              curatorBrandId={curatorBrandId}
              onArchive={isSuperAdmin ? handleArchive : undefined}
              onUnarchive={isSuperAdmin ? handleUnarchive : undefined}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeDragItem ? (
          <div style={{ opacity: 0.9 }}>
            <TendV2ContentCard
              item={activeDragItem}
              brandId={brandId}
              brandName={brandName}
              brandSlug={brandSlug}
              contentId={contentId}
              contentTitle={contentTitle}
              brandDefaultLogo={brandDefaultLogo}
              brandLogoDisplayMode={brandLogoDisplayMode}
              curatorBrandId={curatorBrandId}
              dragEnabled={false}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
    </BrandPaletteProvider>
    </FontLibraryProvider>
  );
};

export default TendedContentGrid;
